import {
  TextField,
  type TextValidatorConfig,
  type Validator,
  type ValidatorProps,
} from '@ace-clublabs/shared-component-library';
import { forwardRef, useEffect, useRef } from 'react';
import Chip from '@/features/appointments/components/Chip';

const AUTO_FOCUS_DELAY_MS = 350; // match scroll animation duration

export type SearchTextFieldValidator = Validator<
  ValidatorProps,
  string,
  TextValidatorConfig<ValidatorProps>
>;

export interface SearchTextFieldProps {
  placeholder?: string;
  placeholderColor?: string | undefined;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedResult?: { title: string } | null;
  onClearSelected?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  validator?: SearchTextFieldValidator;
  autofocus?: boolean;
}

const SearchTextField = forwardRef<HTMLInputElement, SearchTextFieldProps>(
  (
    {
      placeholder,
      placeholderColor,
      value,
      onChange,
      selectedResult,
      onClearSelected,
      onKeyDown,
      startAdornment,
      endAdornment,
      validator,
      autofocus = false,
    },
    ref,
  ) => {
    const localInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (!autofocus) return;
      const timeoutId = window.setTimeout(() => {
        localInputRef.current?.focus();
      }, AUTO_FOCUS_DELAY_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [autofocus]);

    return (
      <TextField
        inputRef={(node) => {
          localInputRef.current = node;

          if (!ref) return;
          if (typeof ref === 'function') {
            ref(node as HTMLInputElement | null);
            return;
          }
          ref.current = node as HTMLInputElement | null;
        }}
        placeholder={selectedResult ? '' : placeholder}
        fullWidth
        showClearButton={false}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        sx={(theme) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
          },
          '&:hover': {
            '& .MuiInputBase-root': {
              backgroundColor: `${theme.palette.primary[100]} !important`,
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: `${placeholderColor} !important`,
            opacity: 1,
          },
        })}
        slotProps={{
          input: {
            startAdornment: selectedResult ? (
              <Chip label={selectedResult.title} onDelete={onClearSelected} />
            ) : (
              startAdornment
            ),
            endAdornment,
          },
          htmlInput: {
            readOnly: !!selectedResult,
          },
        }}
        error={false}
        validationErrorMessage={''}
        validator={validator}
        data-testid='search-text-field'
      />
    );
  },
);

SearchTextField.displayName = 'SearchTextField';

export default SearchTextField;
